import axios from 'axios';
import {
  handleHdriSelected,
  LoadPly,
  makerGlb,
} from '../../pages/Dashboard/GridPage.jsx';
import { BASE_URL } from '../utils.js';
import store from '../../redux/store.js';
import {
  get_grid_assets,
  lightDataUpdate,
  cameraPropertiesUpdate,
  addCamera,
} from '../../redux/Slice/gridSlice.js';
import { DownloadImage } from '../../components/gridpagefunction/image.js';
import { DownloadVideo, applyLoadedVideoTextures } from '../../components/gridpagefunction/Video.js';
import { DownloadMesh } from '../../components/gridpagefunction/Mesh.js';
import { DownloadMeshPly } from '../../components/gridpagefunction/MeshPly.js';
import { DownloadMeshObj } from '../../components/gridpagefunction/MeshObj.js';
import { DownloadMeshFbx } from '../../components/gridpagefunction/MeshFbx.js';
import { DownloadAudio } from '../../components/gridpagefunction/Audio.js';
import { setLoading } from '../../redux/Slice/globalStates.js';
import { UnauthorizedFunction } from '../UtilsFunction.js';
import { handleAddText, loadPublishedTextMeshFromGlb } from '../../components/gridpagefunction/Text.js';
import {
  restoreHotspot,
  ensureHotspotRegistryForScene,
} from '../../components/gridpagefunction/HotSpot.js';import {
  applyLoadedAssetsVisibility,
  syncSceneVisibilityFromStore,
} from './assetVisibilityUtils.js';
import { syncLoadedAssetEvents } from '../eventAssetUtils.js';
import { normalizeLightFromApi } from './lightHelperUtils.js';
import { normalizeCameraFromApi } from './sceneCameraUtils.js';
import {
  getSceneCameraProjectionDefaults,
  hasPersistedSceneCameraProjection,
} from '../../components/gridpagefunction/Camera.js';
import {
  refreshAllLightShadows,
  REFRESH_SCENE_SHADOWS_EVENT,
} from './lightShadowUtils.js';
import {
  handleClouds,
  handleRain,
  handleFire,
  handleSnow,
  handleStars,
  handleFoliage,
  handleTerrain,
  handleWater,
} from '../Elements';
import { addParticle, clearParticles } from '../../redux/Slice/particleSlice';
import { applyParticleVisibility } from './assetVisibilityUtils.js';
import { applyFogToScene } from './fogSceneUtils.js';
import { cleanupClouds } from '../particles/handleClouds';
import { suppressGridHistory } from '../gridHistorySuppress.js';
import { startFreshGridHistory } from '../gridHistorySession.js';
import * as THREE from 'three';
import { setEnvironmentData } from '../../redux/Slice/environmentSlice';
import { addAnimationObject } from '../../redux/Slice/animationSlice.js';
import { loadPhysicsData } from '../../redux/Slice/physicsSlice.js';
import { reconstructParentChildRelationships } from '../../components/layout/gridpage/AppLeftSection/utils/hierarchyUtils.js';
import { getAssetIcon } from '../../components/layout/gridpage/GetAssetIcon.jsx';
import {
  clearHdriEnvironmentAndBackground,
  getActiveViewportCamera,
  syncHdriBackgroundForCamera,
} from './hdriSceneBackground.js';
import { setReferenceObjectVisiblePreference } from '../../components/gridpagefunction/referenceobject.jsx';

// Schema → Frontend
const findIdFromTempFileName = (tempFileName, assetsData) => {
  let id = null;
  let name = null;
  const assetTypes = ['image', 'mesh', 'video', 'audio', 'text', 'particle'];

  for (const type of assetTypes) {
    if (assetsData[type]) {
      for (const item of assetsData[type]) {
        if (item.temp_file_name === tempFileName) {
          id = item._id;
          name = item.file_name || item.name;

          return { id, name };
        }
      }
    }
  }

  return { id, name };
};

// Check if objectId is a temporary or special ID
const isTempOrSpecialId = (objectId) => {
  if (!objectId || typeof objectId !== 'string') return false;
  const prefixes = [
    'temp_',
    'temp-',
    'fire',
    'snow',
    'rain',
    'stars',
    'clouds',
    'terrain',
    'water',
    'foliage',
  ];
  return prefixes.some((prefix) => objectId?.startsWith(prefix));
};

function transformSchemaToFrontend(schemaData, assetsData) {
  const resultArray = schemaData.map((item) => {
    const isTemp = isTempOrSpecialId(item.objectId);
    const resolvedId = isTemp
      ? findIdFromTempFileName(item.objectId, assetsData).id
      : item.objectId;
    // Extract isAnimationActive from the asset data
    let isAnimationActive = true;
    if (assetsData) {
      const assetTypes = ['mesh', 'image', 'video', 'audio', 'text', 'hotspot'];
      for (const type of assetTypes) {
        if (assetsData[type]) {
          const asset = assetsData[type].find(
            (m) =>
              m._id === resolvedId ||
              m.id === resolvedId ||
              m.name === resolvedId ||
              m.url === resolvedId
          );
          if (asset) {
            if (asset.isAnimationActive !== undefined) {
              isAnimationActive = asset.isAnimationActive;
            } else if (asset.events && Array.isArray(asset.events)) {
              // If isAnimationActive is not explicitly defined, check events
              const hasPlayAnimationEvent = asset.events.some(
                (e) => e.action === 'Play Animation'
              );
              if (hasPlayAnimationEvent) {
                isAnimationActive = false;
              }
            }
            break;
          }
        }
      }
    }

    const timelineEffects =
      item.timelineEffects.map((effect) => {
        return {
          ...effect,
          objectId: isTempOrSpecialId(effect.objectId)
            ? findIdFromTempFileName(effect.objectId, assetsData).id
            : effect.objectId,
        };
      }) || [];

    // Resolve missing name or icon from assetsData
    let resolvedName = isTemp
      ? findIdFromTempFileName(item.objectId, assetsData).name
      : item.animationObjectName;

    let foundIcon = '';
    if (assetsData) {
      const types = [
        { key: 'mesh', list: assetsData.mesh },
        { key: 'image', list: assetsData.image },
        { key: 'video', list: assetsData.video },
        { key: 'audio', list: assetsData.audio },
        { key: 'text', list: assetsData.text },
        { key: 'particle', list: assetsData.particle },
        { key: 'hotspots', list: assetsData.hotspots },
      ];

      for (const { key, list } of types) {
        if (!Array.isArray(list)) continue;
        const found = list.find(
          (m) =>
            m._id === resolvedId ||
            m.id === resolvedId ||
            m.temp_file_name === resolvedId ||
            m.url === resolvedId ||
            m.name === resolvedId
        );
        if (found) {
          if (!resolvedName) {
            resolvedName =
              found.file_name ||
              found.name ||
              found.asset_file_name ||
              found.temp_file_name ||
              'Asset';
          }
          foundIcon = getAssetIcon(found, key);
          break;
        }
      }
    }

    if (!resolvedName) {
      resolvedName = isTemp
        ? item.objectId
        : item.animationObjectName || 'Unknown';
    }

    return {
      id: resolvedId,
      name: resolvedName,
      type: '',
      properties: {
        position: true,
        scale: true,
        rotation: true,
      },
      effectType: item.animationEffect,
      icon: foundIcon,
      objectId: resolvedId,
      startTime: item.startTime,
      endTime: item.startTime + item.duration,
      duration: item.duration,
      row: 0,
      easing: item.easing,
      activeAxes: {
        x: item.activeAxes.x,
        y: item.activeAxes.y,
        z: item.activeAxes.z,
      },
      timelineEffects: timelineEffects,
      startPosition: {
        x: item.startPos.x,
        y: item.startPos.y,
        z: item.startPos.z,
      },
      endPosition: {
        x: item.endPos.x,
        y: item.endPos.y,
        z: item.endPos.z,
      },
      loop: item.loop,
      isActive: isAnimationActive,
    };
  });

  // Process nested timeline effects
  resultArray.forEach((item) => {
    if (item.timelineEffects && Array.isArray(item.timelineEffects)) {
      item.timelineEffects = item.timelineEffects.map((effect) => {
        const isEffectTemp = isTempOrSpecialId(effect.objectId);
        const newObjectId = isEffectTemp
          ? findIdFromTempFileName(effect.objectId, assetsData).id
          : effect.objectId;

        return {
          ...effect,
          objectId: newObjectId,
        };
      });
    }
  });

  return resultArray;
}

// Wrapper functions that return promises
const loadImageAssets = (imageData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!imageData || imageData.length === 0) {
        resolve();
        return;
      }

      DownloadImage(imageData, objectt);
      // Wait for images to be processed
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 500);
      }, 500);
    } catch (error) {
      console.error('Error loading images:', error);
      reject(error);
    }
  });
};

const loadVideoAssets = (videoData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!videoData || videoData.length === 0) {
        resolve();
        return;
      }

      DownloadVideo(videoData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 800);
      });
    } catch (error) {
      console.error('Error loading videos:', error);
      reject(error);
    }
  });
};

const loadMeshAssets = (meshData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!meshData || meshData.length === 0) {
        resolve();
        return;
      }

      DownloadMesh(meshData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error('Error loading meshes:', error);
      reject(error);
    }
  });
};

const loadMeshPlyAssets = (meshData, objectt) => {
  console.log(meshData, '-----> meshData-ply');
  return new Promise((resolve, reject) => {
    try {
      if (!meshData || meshData.length === 0) {
        resolve();
        return;
      }

      DownloadMeshPly(meshData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error('Error loading PLY meshes:', error);
      reject(error);
    }
  });
};

const loadMeshObjAssets = (meshData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!meshData || meshData.length === 0) {
        resolve();
        return;
      }

      DownloadMeshObj(meshData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error('Error loading obj meshes:', error);
      reject(error);
    }
  });
};

const loadMeshFbxAssets = (meshData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!meshData || meshData.length === 0) {
        resolve();
        return;
      }

      DownloadMeshFbx(meshData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error('Error loading fbx meshes:', error);
      reject(error);
    }
  });
};

const loadAudioAssets = (audioData, objectt) => {
  return new Promise((resolve, reject) => {
    try {
      if (!audioData || audioData.length === 0) {
        resolve();
        return;
      }

      DownloadAudio(audioData, objectt);
      requestAnimationFrame(() => {
        setTimeout(() => {
          resolve();
        }, 300);
      });
    } catch (error) {
      console.error('Error loading audio:', error);
      reject(error);
    }
  });
};

/**
 * Resolves a hotspot's parent id from the API (e.g. "temp-1773135652184-Cube")
 * to the asset's _id used in the scene after load (e.g. "69afe74fba60065720ad59c8").
 * The backend may store the parent as a pre-publish temp name; loaded assets use _id.
 */
function resolveHotspotParentId(parentId, contentData) {
  if (!parentId || !contentData) return parentId;
  const types = [
    { key: 'mesh', list: contentData.mesh },
    { key: 'image', list: contentData.image },
    { key: 'video', list: contentData.video },
    { key: 'audio', list: contentData.audio },
    { key: 'text', list: contentData.text },
  ];
  for (const { list } of types) {
    if (!Array.isArray(list)) continue;
    const found = list.find(
      (item) =>
        item._id === parentId ||
        item.temp_file_name === parentId ||
        item.id === parentId
    );
    if (found) return found._id || found.temp_file_name || found.id || parentId;
  }
  const possibleFileName = parentId.includes('-')
    ? parentId.split('-').pop()
    : null;
  if (possibleFileName) {
    for (const { list } of types) {
      if (!Array.isArray(list)) continue;
      const byFileName = list.find(
        (item) =>
          item.file_name === possibleFileName || item.file_name === parentId
      );
      if (byFileName)
        return byFileName._id || byFileName.temp_file_name || parentId;
    }
  }
  return parentId;
}

const loadHotspotAssets = (hotspotData, objectt, contentData = null) => {
  return new Promise((resolve, reject) => {
    try {
      if (!hotspotData || hotspotData.length === 0) {
        resolve();
        return;
      }
      hotspotData.forEach((asset) => {
        try {
          const resolved = { ...asset };
          if (resolved.parent && contentData) {
            resolved.parent = resolveHotspotParentId(
              resolved.parent,
              contentData
            );
          }
          restoreHotspot(objectt, resolved);
        } catch (err) {
          console.error('Error restoring hotspot:', asset._id, err);
        }
      });
      requestAnimationFrame(() => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error loading hotspots:', error);
      reject(error);
    }
  });
};

const loadTextAssets = (textData, objectt) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!textData || textData.length === 0) {
        resolve();
        return;
      }

      await TextRenderer(objectt, textData);

      resolve();
    } catch (error) {
      console.error('Error loading text:', error);
      reject(error);
    }
  });
};

function finalizeLoadedSceneShadows(objectt) {
  const scene = objectt?.scene;
  const renderer = objectt?.renderer;
  if (!scene || !renderer?.shadowMap) return;

  const lightData = store.getState().assets_object?.light_data || [];
  refreshAllLightShadows(scene, renderer, lightData);
  try {
    globalThis.dispatchEvent(new CustomEvent(REFRESH_SCENE_SHADOWS_EVENT));
  } catch (_) {}
}

async function finalizeLoadedSceneShadowsDeferred(objectt) {
  finalizeLoadedSceneShadows(objectt);
  await new Promise((resolve) => requestAnimationFrame(resolve));
  finalizeLoadedSceneShadows(objectt);
  await new Promise((resolve) => setTimeout(resolve, 300));
  finalizeLoadedSceneShadows(objectt);
}

// Light data renderer - synchronous operation
const LightDataRender = (response) => {
  try {
    let data = response.data.light_data;
    if (!data) return;

    if (Array.isArray(data)) {
      const normalized = data.map((light) =>
        normalizeLightFromApi({
          ...light,
          temp_file_name:
            light.temp_file_name?.length > 0 ? light.temp_file_name : light._id,
        })
      );
      store.dispatch(lightDataUpdate(normalized));
    } else if (typeof data === 'object' && data !== null) {
      store.dispatch(
        lightDataUpdate([
          normalizeLightFromApi({
            ...data,
            temp_file_name:
              data.temp_file_name?.length > 0 ? data.temp_file_name : data._id,
          }),
        ])
      );
    }
  } catch (error) {
    console.error('Error rendering light data:', error);
  }
};

function normalizeParticleForLoad(particle) {
  if (!particle) return particle;
  const type =
    particle.type || particle.name?.split('_')[0]?.toLowerCase() || '';
  if (type !== 'clouds' && type !== 'cloud') return particle;
  const clusterCount = particle.clusterCount ?? particle.cloudCount ?? 15;
  return {
    ...particle,
    clusterCount,
    cloudCount: clusterCount,
  };
}

function removeParticleSystemsFromScene(scene) {
  if (!scene) return;
  const toRemove = scene.children.filter(
    (child) => child.deleteType === 'particle'
  );
  toRemove.forEach((obj) => {
    if (obj.objType === 'clouds') {
      cleanupClouds(obj);
    }
    scene.remove(obj);
    obj.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  });
}

// Particle data renderer - synchronous operation
const ParticleDataRender = (scene, particleData) => {
  try {
    // Clear Redux + scene particles before re-rendering to prevent duplicates
    store.dispatch(clearParticles());
    removeParticleSystemsFromScene(scene);

    if (!Array.isArray(particleData) || particleData.length === 0) return;

    const particleHandlers = {
      clouds: handleClouds,
      rain: handleRain,
      fire: handleFire,
      snow: handleSnow,
      stars: handleStars,
      foliage: handleFoliage,
      terrain: handleTerrain,
      water: handleWater,
    };

    particleData.forEach((particle) => {
      const normalized = normalizeParticleForLoad(particle);
      const particleType =
        normalized.type || normalized.name.split('_')[0].toLowerCase();

      const handler = particleHandlers[particleType];
      if (handler) {
        handler(scene, normalized);
        const particleId = normalized._id || normalized.id;
        const newParticle = {
          ...normalized,
          id: particleId,
          // ═══════ FIX: Remove explicit name to allow Redux naming increment logic ═══════
          // Don't pass name - let Redux reducer generate incremented names (Fire, Fire 1, Fire 2, etc.)
          type: particle.type || particle.name.split('_')[0].toLowerCase(),
          isPlaying:
            particle.isPlaying !== undefined ? particle.isPlaying : true,
          isStopped:
            particle.isStopped !== undefined ? particle.isStopped : false,
          visible: normalized.visible !== false,
          isDuplicate: false, // Loaded from API — keep saved names and counts
        };
        store.dispatch(addParticle(newParticle));

        if (normalized.visible === false && particleId) {
          const sceneObj = scene.getObjectByName(particleId);
          if (sceneObj) {
            applyParticleVisibility(sceneObj, false);
          }
        }
      }
    });
  } catch (error) {
    console.error('Error rendering particle data:', error);
  }
};

// Camera setup - synchronous operation
const setupCameras = (cameraData, scene) => {
  try {
    if (!cameraData || !scene) return;

    // Clone data to avoid mutating frozen objects
    const clonedCameraData = cameraData.map((cam) =>
      normalizeCameraFromApi({
        ...cam,
        temp_file_name:
          cam.temp_file_name && cam.temp_file_name.length > 0
            ? cam.temp_file_name
            : cam._id || cam.id,
      })
    );

    // Clear existing cameras in Redux
    store.dispatch({
      type: 'data/clearAssets',
      payload: ['camera_properties'],
    });

    clonedCameraData.forEach((cam) => {
      const stableId = cam._id || cam.id;
      const normalizedCam = normalizeCameraFromApi({
        ...cam,
        _id: stableId,
        id: stableId,
      });
      const projection = getSceneCameraProjectionDefaults(normalizedCam);
      const cameraRecord = {
        ...normalizedCam,
        near: projection.near,
        far: projection.far,
        fov: projection.fov,
        projectionEdited: hasPersistedSceneCameraProjection(normalizedCam),
      };
      store.dispatch(addCamera(cameraRecord));

      let cameraObj = scene.getObjectByName(stableId);

      if (!cameraObj) {
        cameraObj = new THREE.PerspectiveCamera(
          projection.fov,
          window.innerWidth / window.innerHeight,
          projection.near,
          projection.far
        );

        cameraObj.name = stableId;

        if (cam.position) {
          cameraObj.position.set(
            cam.position.x,
            cam.position.y,
            cam.position.z
          );
        }

        if (cam.rotation) {
          cameraObj.rotation.set(
            cam.rotation.x,
            cam.rotation.y,
            cam.rotation.z
          );
        }

        scene.add(cameraObj);
      } else {
        if (cam.position) {
          cameraObj.position.set(
            cam.position.x ?? 0,
            cam.position.y ?? 0,
            cam.position.z ?? 0
          );
        }
        if (cam.rotation) {
          cameraObj.rotation.set(
            cam.rotation.x ?? 0,
            cam.rotation.y ?? 0,
            cam.rotation.z ?? 0
          );
        }
      }
    });
  } catch (error) {
    console.error('Error setting up cameras:', error);
  }
};

// Environment setup - synchronous operation
const setupEnvironment = (environmentData) => {
  try {
    if (!environmentData) {
      // Set default environment
      store.dispatch(
        setEnvironmentData({
          skybox: { name: 'Color', url: null },
          hdriFile: '',
          hdriRotation: 0,
          hdriVisible: false,
          fog_checked: false,
          fogType: 'Linear',
          fogStart: 1,
          fogEnd: 100,
          fog_density: 0.01,
          color_config: {
            color_type: 'Single',
            color: '#2D2E32',
            top_color: '',
            bottom_color: '',
          },
        })
      );

      return;
    }

    const environmentPayload = {
      skybox: environmentData.skybox || { name: 'Color', url: null },
      hdriFile: environmentData.hdriFile || environmentData.skybox?.url || '',
      hdriRotation: Number(environmentData.hdriRotation) || 0,
      hdriVisible: environmentData.hdriVisible !== false,
      hdriLightIntensity: Number(environmentData.hdriLightIntensity ?? 1),
      fog_checked: environmentData.fog_checked || false,
      fogType: environmentData.fogType || 'Linear',
      // Always persist fogColor so EnvironmentComponent never falls back to
      // reading scene.fog.color (which could be black after a bad load).
      fogColor: environmentData.fogColor || '#ffffff',
      fogStart: Number(environmentData.fogStart) || 1,
      fogEnd: Number(environmentData.fogEnd) || 100,
      fog_density: Number(environmentData.fog_density) || 0.01,
      color_config: {
        ...(environmentData.color_config || {
          color_type: 'Single',
          color: '#2D2E32',
          top_color: '',
          bottom_color: '',
        }),
        color:
          environmentData.color_config?.color?.trim() === ''
            ? '#2D2E32'
            : environmentData.color_config?.color || '#2D2E32',
      },
    };

    store.dispatch(setEnvironmentData(environmentPayload));
  } catch (error) {
    console.error('Error setting up environment:', error);
  }
};

// Apply HDRI to scene
const applyHdriToScene = (environmentData, scene) => {
  try {
    if (!environmentData || !scene) return;

    const hasHdriUrl = environmentData.skybox?.url || environmentData.hdriFile;
    const isHdriVisible = environmentData.hdriVisible !== false;

    if (hasHdriUrl && isHdriVisible) {
      const url =
        environmentData.hdriFile ||
        environmentData.skybox?.url ||
        environmentData.skybox?.hdriFile;
      const rotation = Number(environmentData.hdriRotation || 0);
      const lightIntensity = Number(environmentData.hdriLightIntensity ?? 1);

      if (url) {
        const activeCamera = getActiveViewportCamera();
        handleHdriSelected(
          '',
          url,
          rotation,
          scene,
          () => syncHdriBackgroundForCamera(scene, getActiveViewportCamera()),
          true,
          activeCamera,
          lightIntensity
        );
      }
    } else if (hasHdriUrl && !isHdriVisible) {
      clearHdriEnvironmentAndBackground(scene, new THREE.Color(0x2d2e32));
    }
  } catch (error) {
    console.error('Error applying HDRI:', error);
  }
};

export { applyFogToScene };

// Apply background color/gradient
const applyBackgroundToScene = (environmentData, scene) => {
  try {
    if (!environmentData || !scene) return;

    const isSkyboxColor = environmentData.skybox?.name === 'Color';
    const hasColorConfig = environmentData.color_config;
    const colorType = environmentData.color_config?.color_type;

    if (isSkyboxColor && hasColorConfig && colorType && colorType !== 'None') {
      const { color_type, color, top_color, bottom_color } =
        environmentData.color_config;

      if (color_type === 'Single' && color) {
        scene.background = new THREE.Color(color);
      } else if (color_type === 'Gradient' && top_color && bottom_color) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, top_color);
        gradient.addColorStop(1, bottom_color);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const texture = new THREE.CanvasTexture(canvas);
        scene.background = texture;
      }
    }
  } catch (error) {
    console.error('Error applying background:', error);
  }
};

// Handle GLB/PLY files
const handleGlbPlyFiles = async (filePaths) => {
  try {
    if (!filePaths || filePaths.length === 0) return;

    for (const filePath of filePaths) {
      const extension = filePath.split('.').pop();

      if (extension === 'ply') {
        await LoadPly(filePath);
        localStorage.setItem('ply', filePath);
      } else if (extension === 'glb') {
        await makerGlb(filePath);
        localStorage.setItem('glb', filePath);
      }
    }
  } catch (error) {
    console.error('Error handling GLB/PLY files:', error);
  }
};

// Load animation effects - must be called AFTER all assets are loaded
const loadAnimationEffects = async (animationData, assetsData, scene) => {
  return new Promise((resolve) => {
    try {
      if (!animationData || !scene) {
        resolve();
        return;
      }

      // Wait for scene to be fully ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          const frontendData = transformSchemaToFrontend(
            animationData,
            assetsData
          );

          frontendData.forEach((animObj) => {
            store.dispatch(addAnimationObject(animObj));
          });

          resolve();
        }, 1500);
      });
    } catch (error) {
      console.error('Error loading animation effects:', error);
      resolve();
    }
  });
};

// Text renderer - now properly async
export const TextRenderer = async (object, textData) => {
  try {
    if (!textData || textData.length === 0) return;

    const textPromises = textData.map(async (text) => {
      const tm = text?.text_material || {};
      const glbUrl = text?.asset_file_path || text?.asset_file_pathh;
      // Published text GLB already bakes pivot compensation + scale — prefer it.
      if (glbUrl) {
        const loaded = await loadPublishedTextMeshFromGlb(object, text);
        if (loaded) return loaded;
      }

      const pivotMatrix =
        text?.pivot_content_matrix || tm.pivot_content_matrix;
      const pivotOffset = text?.pivot_offset || tm.pivot_offset;
      const pivotEdited = !!(
        text?.pivot_edited ||
        tm.pivot_edited ||
        (Array.isArray(pivotMatrix) && pivotMatrix.length === 16) ||
        pivotOffset
      );
      const obj = {
        ...tm,
        position: {
          x: text?.position_x || 0,
          y: text?.position_y || 0,
          z: text?.position_z || 0,
        },
        rotation: {
          x: text?.rotation_x || 0,
          y: text?.rotation_y || 0,
          z: text?.rotation_z || 0,
        },
        scale: {
          x: text?.scale_x ?? 1,
          y: text?.scale_y ?? 1,
          z: text?.scale_z ?? 1,
        },
        bakedTextScale: tm.bakedTextScale,
        file_name: text.file_name,
        temp_file_name: text.temp_file_name || text.name,
        _id: text._id || text.id,
        id: text._id || text.id,
        visible: text.visible,
        events: text.events,
        textType: tm.textType,
        pivot_edited: pivotEdited,
        pivot_content_matrix: pivotMatrix,
        pivot_offset: pivotOffset,
        asset_file_path: glbUrl,
      };
      await handleAddText(object, obj);
    });

    await Promise.all(textPromises);
  } catch (error) {
    console.error('Error rendering text:', error);
  }
};

// Main load content function - OPTIMIZED
async function LoadContent(id, setName, objectt, options = {}) {
  const { silent, setCategory, setRedirectionCode } = options;

  // New session while loading — no undo of empty/partial load state.
  startFreshGridHistory({ ready: false });
  const endHistorySuppress = suppressGridHistory();
  if (!silent) store.dispatch(setLoading(true));

  const getExperienceAPI = `${BASE_URL}/assets/project/${id}/assets`;

  try {
    // Step 1: Fetch data from API
    const res = await axios({
      method: 'get',
      url: getExperienceAPI,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    const response = res.data;

    if (!res.data.success) {
      throw new Error('API request was not successful');
    }

    // Step 2: Update Redux with raw data
    store.dispatch(get_grid_assets(response));

    // Step 3: Set project metadata
    setName(res.data.data.project_name);
    if (setCategory && res.data.data.category) {
      setCategory(res.data.data.category.toLowerCase());
    }

    // Reference (dummy) character visibility saved on the experience. The
    // character mounts async after category is set, so record the preference;
    // it's applied immediately if the object already exists, else on mount.
    setReferenceObjectVisiblePreference(
      res.data.data.reference_object_visible,
      objectt?.scene
    );

    if (setRedirectionCode && res.data.data.redirection_code) {
      setRedirectionCode(res.data.data.redirection_code);
    }

    localStorage.setItem('custom_id', res.data.data?.custom_id);

    const contentData = res.data.data;

    // Step 4: Load all assets in parallel (except animations)

    await Promise.all([
      loadImageAssets(contentData.image, objectt),
      loadVideoAssets(contentData.video, objectt),
      loadMeshAssets(contentData.mesh, objectt),
      loadMeshPlyAssets(
        contentData.mesh.filter((item) => item.glb_type === 'ply'),
        objectt
      ),
      loadMeshObjAssets(
        contentData.mesh.filter((item) => item.glb_type === 'obj'),
        objectt
      ),
      loadMeshFbxAssets(
        contentData.mesh.filter((item) => item.glb_type === 'fbx'),
        objectt
      ),
      loadAudioAssets(contentData.audio, objectt),
      loadTextAssets(contentData.text, objectt),
      loadHotspotAssets(contentData.hotspots || [], objectt, contentData),
    ]);

    // Apply video assets referenced by mesh/shape video_texture after both are loaded
    applyLoadedVideoTextures(
      objectt?.scene,
      contentData.mesh || [],
      contentData.video || []
    );
    // Retry once meshes/videos finish async GLB / video metadata load
    setTimeout(() => {
      applyLoadedVideoTextures(
        objectt?.scene,
        contentData.mesh || [],
        contentData.video || []
      );
    }, 1500);

    // Step 5: Setup lights (synchronous)
    LightDataRender(response);

    // Step 6: Setup particles (synchronous)
    if (contentData.particle && objectt.scene) {
      ParticleDataRender(objectt.scene, contentData.particle);
    }

    // Step 6.5: Load physics data from backend
    if (contentData.physics_data || contentData.physics_world_settings) {
      store.dispatch(
        loadPhysicsData({
          physicsData: contentData.physics_data || [],
          worldSettings: contentData.physics_world_settings || {},
        })
      );
    }

    // Step 7: Setup cameras (synchronous)
    if (contentData.camera_properties && objectt.scene) {
      setupCameras(contentData.camera_properties, objectt.scene);
    }

    if (objectt.scene) {
      applyLoadedAssetsVisibility(objectt.scene, contentData);
    }

    // Step 8: Setup environment (synchronous)
    setupEnvironment(contentData.environment);

    // Step 9: Apply HDRI to scene
    if (objectt.scene) {
      applyHdriToScene(contentData.environment, objectt.scene);
    }

    // Step 10: Apply fog to scene
    if (contentData.environment && objectt.scene) {
      applyFogToScene(objectt.scene, contentData.environment);
    }

    // Step 11: Apply background color/gradient
    if (objectt.scene) {
      applyBackgroundToScene(contentData.environment, objectt.scene);
    }

    // Step 12: Handle GLB/PLY files
    const filePaths = res.data.data.movedFilesPaths || [];
    await handleGlbPlyFiles(filePaths);

    // Step 13: Reconstruct Parent-Child Relationships
    if (objectt.scene) {
      reconstructParentChildRelationships(
        objectt.scene,
        response.data,
        contentData.light_data,
        contentData.camera_properties,
        contentData.particle
      );
      syncLoadedAssetEvents(objectt.scene, contentData);
    }

    // Step 14: Load animation effects LAST (after all objects are in scene)
    if (contentData.animation_effects && objectt.scene) {
      await loadAnimationEffects(
        contentData.animation_effects,
        contentData,
        objectt.scene
      );
    }

    if (objectt.scene) {
      syncSceneVisibilityFromStore(objectt.scene);
    }

    finalizeLoadedSceneShadows(objectt);

    // Step 15: Final poly count check
    if (objectt.polyCount === 0) {
      objectt.setPolyCount(1);
    }
  } catch (err) {
    console.error('Error in LoadContent:', err);
    UnauthorizedFunction(err?.response?.data);
  } finally {
    await finalizeLoadedSceneShadowsDeferred(objectt);
    // Small delay to ensure rendering is complete
    await new Promise((resolve) => setTimeout(resolve, 200));
    endHistorySuppress();
    // Fresh empty history — user edits after this start at step 0, 1, 2, …
    startFreshGridHistory({ ready: true });
    if (!silent) store.dispatch(setLoading(false));
  }
}

// Preview load function - OPTIMIZED
async function LoadContentPreview(id, setName, objectt, nobars, options = {}) {
  const silent = options.silent;

  startFreshGridHistory({ ready: false });
  const endHistorySuppress = suppressGridHistory();
  if (!silent) store.dispatch(setLoading(true));

  const getExperienceAPI = `${BASE_URL}/assets/project/${id}/assets${nobars ? '-previews' : ''}`;
  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  try {
    // Step 1: Fetch data from API
    const res = await axios({
      method: 'get',
      url: getExperienceAPI,
      headers: authHeaders,
    });

    const response = res.data;

    if (!res.data.success) {
      throw new Error('API request was not successful');
    }

    // Step 2: Update Redux with raw data
    store.dispatch(get_grid_assets(response));

    // Step 3: Set project metadata
    setName(res.data.data.project_name);
    localStorage.setItem('custom_id', res.data.data?.custom_id);

    // Apply saved reference (dummy) character visibility from the experience.
    setReferenceObjectVisiblePreference(
      res.data.data.reference_object_visible,
      objectt?.scene
    );

    const contentData = res.data.data;

    // When preview API omits audio, fetch full assets so audio works in preview mode
    let audioData = contentData.audio;
    if (nobars && token && (!audioData || audioData.length === 0)) {
      try {
        const fullRes = await axios({
          method: 'get',
          url: `${BASE_URL}/assets/project/${id}/assets`,
          headers: authHeaders,
        });
        if (fullRes.data?.success && fullRes.data?.data?.audio?.length) {
          audioData = fullRes.data.data.audio;
        }
      } catch (_) {}
    }

    // Preview API can return incomplete hotspot message/interaction fields — merge full data.
    let hotspotData = contentData.hotspots || [];
    const hotspotNeedsFullData =
      nobars &&
      token &&
      (!hotspotData.length ||
        hotspotData.some(
          (h) =>
            !h?.messageText ||
            h.functionType == null ||
            h.messagePopupType == null ||
            h.messageOpacity == null
        ));
    if (hotspotNeedsFullData) {
      try {
        const fullRes = await axios({
          method: 'get',
          url: `${BASE_URL}/assets/project/${id}/assets`,
          headers: authHeaders,
        });
        const fullHotspots = fullRes.data?.data?.hotspots;
        if (fullRes.data?.success && fullHotspots?.length) {
          if (!hotspotData.length) {
            hotspotData = fullHotspots;
          } else {
            const fullByKey = new Map();
            fullHotspots.forEach((h) => {
              const key = String(h._id || h.id || h.temp_file_name || '');
              if (key) fullByKey.set(key, h);
            });
            hotspotData = hotspotData.map((previewHs) => {
              const key = String(
                previewHs._id || previewHs.id || previewHs.temp_file_name || ''
              );
              const fullHs = fullByKey.get(key);
              if (!fullHs) return previewHs;
              return {
                ...fullHs,
                ...previewHs,
                messageText: previewHs.messageText || fullHs.messageText,
                functionType: previewHs.functionType ?? fullHs.functionType,
                messagePopupType:
                  previewHs.messagePopupType ?? fullHs.messagePopupType,
                messageOpacity:
                  previewHs.messageOpacity ?? fullHs.messageOpacity,
                messageBgColor:
                  previewHs.messageBgColor || fullHs.messageBgColor,
                messageTextColor:
                  previewHs.messageTextColor || fullHs.messageTextColor,
                buttonText: previewHs.buttonText || fullHs.buttonText,
                focusDistance: previewHs.focusDistance ?? fullHs.focusDistance,
              };
            });
          }
        }
      } catch (_) {}
    }

    // Step 4: Load all assets in parallel
    await Promise.all([
      loadImageAssets(contentData.image, objectt),
      loadVideoAssets(contentData.video, objectt),
      loadMeshAssets(contentData.mesh, objectt),
      loadAudioAssets(audioData || [], objectt),
      loadTextAssets(contentData.text, objectt),
      loadHotspotAssets(hotspotData, objectt, contentData),
    ]);

    applyLoadedVideoTextures(
      objectt?.scene,
      contentData.mesh || [],
      contentData.video || []
    );
    setTimeout(() => {
      applyLoadedVideoTextures(
        objectt?.scene,
        contentData.mesh || [],
        contentData.video || []
      );
    }, 1500);

    // Step 5: Setup lights
    LightDataRender(response);

    // Step 6: Setup particles
    if (contentData.particle && objectt.scene) {
      ParticleDataRender(objectt.scene, contentData.particle);
    }

    // Step 6.5: Load physics data from backend
    if (contentData.physics_data || contentData.physics_world_settings) {
      store.dispatch(
        loadPhysicsData({
          physicsData: contentData.physics_data || [],
          worldSettings: contentData.physics_world_settings || {},
        })
      );
    }

    // Step 7: Setup cameras (same as editor load — do not pass array to cameraPropertiesUpdate)
    if (contentData.camera_properties && objectt.scene) {
      setupCameras(contentData.camera_properties, objectt.scene);
    }

    if (objectt.scene) {
      applyLoadedAssetsVisibility(objectt.scene, contentData);
    }

    // Step 8: Setup environment
    setupEnvironment(contentData.environment);

    // Step 9: Apply HDRI to scene
    if (objectt.scene) {
      applyHdriToScene(contentData.environment, objectt.scene);
    }

    // Step 10: Apply fog to scene
    if (contentData.environment && objectt.scene) {
      applyFogToScene(objectt.scene, contentData.environment);
    }

    // Step 11: Apply background
    if (objectt.scene) {
      applyBackgroundToScene(contentData.environment, objectt.scene);
    }

    // Step 12: Reconstruct Parent-Child Relationships
    if (objectt.scene) {
      reconstructParentChildRelationships(
        objectt.scene,
        response.data,
        contentData.light_data,
        contentData.camera_properties,
        contentData.particle
      );
      syncLoadedAssetEvents(objectt.scene, contentData);
    }

    // Step 13: Load animation effects LAST (after all objects are in scene)
    if (contentData.animation_effects && objectt.scene) {
      await loadAnimationEffects(
        contentData.animation_effects,
        contentData,
        objectt.scene
      );
    }

    if (objectt.scene) {
      syncSceneVisibilityFromStore(objectt.scene);
    }

    if (objectt.camera && objectt.renderer) {
      const loadedHotspots =
        store.getState().assets_object?.sceneHotspots || [];
      ensureHotspotRegistryForScene(
        loadedHotspots,
        objectt.camera,
        objectt.renderer
      );
    }

    finalizeLoadedSceneShadows(objectt);

    // Step 14: Final poly count check
    if (objectt.polyCount === 0) {
      objectt.setPolyCount(1);
    }
  } catch (err) {
    console.error('Error in LoadContentPreview:', err);
    // Shared /preview-page routes are public — do not force login on load errors.
    if (!nobars) {
      UnauthorizedFunction(err?.response?.data);
    }
  } finally {
    await finalizeLoadedSceneShadowsDeferred(objectt);
    await new Promise((resolve) => setTimeout(resolve, 200));
    endHistorySuppress();
    startFreshGridHistory({ ready: true });
    if (!silent) store.dispatch(setLoading(false));
  }
}

export { LoadContent, LoadContentPreview };
